import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { Download, OpenInNew, KeyboardArrowDown, Refresh } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { getHostTenantSlug } from '../../utils/tenantResolver';
import {
  getManageAccountCacheKey,
  readManageAccountCache,
  writeManageAccountCache,
} from './manageAccountSessionCache';

interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: string;
  invoiceNumber: string;
  date: number;
  dueDate: number | null;
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
  receiptPdf?: string | null;
  description?: string | null;
}

const Invoices: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const invoicesCacheKey = getManageAccountCacheKey(null, getHostTenantSlug());
  const cachedInvoices = readManageAccountCache<Invoice[]>('invoices', invoicesCacheKey);
  const [invoices, setInvoices] = useState<Invoice[]>(cachedInvoices ?? []);
  const [loading, setLoading] = useState(!cachedInvoices);
  const [error, setError] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [displayCount, setDisplayCount] = useState(5);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const ITEMS_PER_PAGE = 5;

  const updateInvoiceInState = useCallback((invoiceId: string, updates: Partial<Invoice>) => {
    setInvoices((prev) =>
      prev.map((inv) => (inv.id === invoiceId ? { ...inv, ...updates } : inv))
    );
  }, []);

  const loadInvoices = useCallback(async (showRefreshing = false) => {
    const hasCache = !!readManageAccountCache<Invoice[]>('invoices', invoicesCacheKey);
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else if (!hasCache) {
        setLoading(true);
      }
      setError(null);
      console.log('Invoices: Loading invoices...');
      const response = await apiService.getInvoices(100); // Increased limit to capture all invoices including subscription changes
      console.log('Invoices: Response received:', response);
      console.log('Invoices: Full response data:', JSON.stringify(response, null, 2));
      
      if (response.invoices) {
        console.log('Invoices: Found', response.invoices.length, 'invoices');
        // Log each invoice for debugging
        response.invoices.forEach((inv: any, idx: number) => {
          console.log(`Invoices: Invoice ${idx + 1}:`, {
            id: inv.id,
            description: inv.description,
            amount: inv.amount,
            status: inv.status,
            date: inv.date,
            subscription: inv.subscription
          });
        });
        
        setInvoices(response.invoices);
        writeManageAccountCache('invoices', invoicesCacheKey, response.invoices);
        // Empty history is normal — show it in the page body, not as a red error banner.
        setError(null);
      } else if (response.error) {
        console.error('Invoices: Error from API:', response.error);
        const errMsg = String(response.error);
        if (/invalid api key|no api key provided/i.test(errMsg)) {
          setInvoices([]);
          setError(null);
        } else {
          setError(response.error);
        }
      } else {
        console.warn('Invoices: Unexpected response format:', response);
        setError('Unexpected response format. Please try again.');
      }
    } catch (err: any) {
      console.error('Invoices: Exception loading invoices:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load invoices';
      setError(errorMessage);
      // If it's a 401/403, it might mean the organization doesn't have a Stripe customer yet
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Unable to load invoices. Please ensure you have completed a payment first.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [invoicesCacheKey]);

  // Initial load: fetch invoices when the tab is opened
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Refresh when returning from Stripe Billing Portal (e.g. after plan change Professional → Standard)
  useEffect(() => {
    if (searchParams.get('refresh') === 'true') {
      setDisplayCount(ITEMS_PER_PAGE);
      loadInvoices(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('refresh');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, loadInvoices, setSearchParams]);

  // Reset display count when month filter changes
  useEffect(() => {
    setDisplayCount(ITEMS_PER_PAGE);
  }, [selectedMonth]);

  // Auto-hide error message after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000); // 10 seconds

      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleRefresh = () => {
    console.log('Invoices: Manual refresh triggered');
    // Reset filter and pagination on refresh
    setSelectedMonth('all');
    setDisplayCount(ITEMS_PER_PAGE);
    setError(null);
    loadInvoices(true);
  };

  const handleViewDetails = async (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDetailsDialogOpen(true);
    setLoadingDetails(true);
    setInvoiceDetails(null);

    try {
      const response = await apiService.getInvoice(invoice.id);
      if (response.error) {
        setError(response.error);
      } else {
        setInvoiceDetails(response);
        // Keep the row in sync with the latest status/links from Stripe
        updateInvoiceInState(invoice.id, {
          status: response.status || invoice.status,
          invoicePdf: response.invoicePdf ?? invoice.invoicePdf,
          hostedInvoiceUrl: response.hostedInvoiceUrl ?? invoice.hostedInvoiceUrl,
          receiptPdf: response.receiptPdf ?? invoice.receiptPdf,
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load invoice details');
    } finally {
      setLoadingDetails(false);
    }
  };

  /** Opens our project's invoice details dialog (project-generated PDF only, no Stripe PDF/Pay online) */
  const handleViewInvoice = async (invoice: Invoice) => {
    try {
      const response = await apiService.getInvoice(invoice.id);
      if (!response.error) {
        updateInvoiceInState(invoice.id, {
          status: response.status || invoice.status,
          invoicePdf: response.invoicePdf ?? invoice.invoicePdf,
          hostedInvoiceUrl: response.hostedInvoiceUrl ?? invoice.hostedInvoiceUrl,
          receiptPdf: response.receiptPdf ?? invoice.receiptPdf,
        });
      }
    } catch {
      // ignore
    }
    handleViewDetails(invoice);
  };

  const handleDownloadPdf = async (invoiceId: string, invoiceNumber: string) => {
    try {
      const result = await apiService.downloadInvoicePdf(invoiceId, invoiceNumber);
      if (!result.success && result.error) {
        setError(result.error);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to download invoice PDF');
    }
  };

  const handleDownloadReceipt = async (invoice: Invoice) => {
    try {
      const result = await apiService.downloadReceiptPdf(invoice.id, invoice.invoiceNumber);
      if (!result.success && result.error) {
        setError(result.error);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to download receipt PDF');
    }
  };

  const handleManageInStripe = async () => {
    try {
      setLoadingPortal(true);
      setError(null);
      const returnUrl = window.location.href;
      const response = await apiService.createBillingPortalSession(returnUrl);
      
      if (response.url) {
        // Successfully created/accessed billing portal - redirect to Stripe
        window.location.href = response.url;
      } else if (response.error) {
        // Show error but don't keep loading state
        setError(response.error);
        setLoadingPortal(false);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to open Stripe billing portal. Please try again.';
      if (/invalid api key|no api key provided/i.test(String(errorMessage))) {
        setError('Stripe is not configured on this server. Contact your administrator.');
      } else {
        setError(errorMessage);
      }
      setLoadingPortal(false);
    }
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'open':
        return 'warning';
      case 'draft':
        return 'default';
      case 'void':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Generate month/year options from invoices
  const monthYearOptions = useMemo(() => {
    const options = new Set<string>();
    invoices.forEach((invoice) => {
      if (invoice.date) {
        const date = new Date(invoice.date * 1000);
        const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
        options.add(monthYear);
      }
    });
    return Array.from(options).sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB.getTime() - dateA.getTime();
    });
  }, [invoices]);

  // Filter invoices by selected month/year, then sort by date descending (latest first)
  const filteredInvoices = useMemo(() => {
    let list = invoices;
    if (selectedMonth !== 'all') {
      list = invoices.filter((invoice) => {
        if (!invoice.date) return false;
        const date = new Date(invoice.date * 1000);
        const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
        return monthYear === selectedMonth;
      });
    }
    return [...list].sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
  }, [invoices, selectedMonth]);

  // Display only first N invoices (Load More pagination)
  const displayedInvoices = useMemo(
    () => filteredInvoices.slice(0, displayCount),
    [filteredInvoices, displayCount]
  );
  const hasMoreInvoices = displayCount < filteredInvoices.length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Billing & Invoices
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Billing & Invoices
              </Typography>
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
                title="Refresh invoices"
                sx={{ color: 'primary.main' }}
              >
                <Refresh />
              </IconButton>
            </Box>
            <Button
              variant="outlined"
              onClick={handleManageInStripe}
              disabled={loadingPortal}
              endIcon={<OpenInNew />}
              sx={{ minWidth: 160 }}
            >
              {loadingPortal ? 'Loading...' : 'Manage in Stripe'}
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Invoice History
          </Typography>
          {monthYearOptions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 220, maxWidth: 280 }}>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                displayEmpty
                IconComponent={KeyboardArrowDown}
                renderValue={(value) => {
                  const label = value === 'all' ? 'All Invoices' : String(value);
                  return (
                    <Box
                      component="span"
                      title={label}
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </Box>
                  );
                }}
                MenuProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                  transformOrigin: { vertical: 'top', horizontal: 'right' },
                  PaperProps: {
                    sx: { minWidth: 220, maxWidth: 320 },
                  },
                }}
                sx={{
                  '& .MuiSelect-select': {
                    py: 1.5,
                  },
                }}
              >
                <MenuItem value="all" title="All Invoices">
                  All Invoices
                </MenuItem>
                {monthYearOptions.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    title={option}
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        {filteredInvoices.length === 0 && !loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No invoices found. Invoices will appear here after you make a payment.
            </Typography>
          </Box>
        ) : (
        <>
        <TableContainer
          sx={{
            width: '100%',
            maxWidth: '100%',
            position: 'relative',
            overflow: 'hidden',  // contain scroll to inner Box
          }}
        >
          <Box
            sx={{
              height: 176,
              minHeight: 176,
              overflowY: 'auto',
              overflowX: 'auto',
              width: '100%',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              overscrollBehavior: 'contain',
              scrollbarGutter: 'stable',
              position: 'relative',
              transform: 'translateZ(0)',  // GPU layer for reliable touch scroll on iOS
            }}
          >
          <Table
            stickyHeader
            size="small"
            sx={{
              width: '100%',
              minWidth: 800,
              tableLayout: 'fixed',
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '15%' }}>Date</TableCell>
                <TableCell sx={{ width: '45%' }}>Description</TableCell>
                <TableCell sx={{ width: '15%' }}>Status</TableCell>
                <TableCell sx={{ width: '15%' }}>Timestamp</TableCell>
                <TableCell sx={{ width: '15%' }}>Amount</TableCell>
                {/* Move Invoice column slightly more to the right by giving it a bit more width and right padding */}
                <TableCell align="right" sx={{ width: '15%', whiteSpace: 'nowrap', pr: 4 }}>
                  Invoice
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedInvoices.map((invoice) => (
                <TableRow key={invoice.id} hover>
                  <TableCell>
                    {invoice.date ? formatDate(invoice.date) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {invoice.description || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      color={getStatusColor(invoice.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {invoice.date ? formatTime(invoice.date) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {formatCurrency(invoice.amount, invoice.currency)}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 4 }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNew />}
                        onClick={() => handleViewInvoice(invoice)}
                      >
                        View
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        </TableContainer>
        {hasMoreInvoices && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setDisplayCount((prev) => prev + ITEMS_PER_PAGE)}
            >
              Load More
            </Button>
          </Box>
        )}
        </>
        )}
        </CardContent>
      </Card>

      {/* Invoice Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 6,
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: '80px',
            paddingBottom: '24px',
            paddingLeft: '16px',
            paddingRight: '16px',
            boxSizing: 'border-box',
          },
          '& .MuiDialog-paper': {
            width: 'min(900px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))',
            maxWidth: 'min(900px, calc(100vw - clamp(172px, 13.4375vw, 280px) - 48px))',
            maxHeight: 'calc(100vh - 104px)',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxSizing: 'border-box',
          },
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>
          Invoice Details
          {selectedInvoice && (
            <Typography variant="body2" color="text.secondary">
              #{selectedInvoice.invoiceNumber || (selectedInvoice.id ? selectedInvoice.id.substring(0, 8) : '')}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : invoiceDetails ? (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={invoiceDetails.status?.toUpperCase()}
                  color={getStatusColor(invoiceDetails.status)}
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(invoiceDetails.amount || 0, invoiceDetails.currency || 'USD')}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date
                </Typography>
                <Typography>
                  {invoiceDetails.date
                    ? new Date(invoiceDetails.date * 1000).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Box>

              {invoiceDetails.lineItems && invoiceDetails.lineItems.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Line Items
                  </Typography>
                  <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceDetails.lineItems.map((item: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>{item.description || 'N/A'}</TableCell>
                            <TableCell align="right">{item.quantity || 1}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(item.amount || 0, invoiceDetails.currency || 'USD')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          {invoiceDetails?.status === 'paid' && invoiceDetails?.id && (
            <Button
              startIcon={<Download />}
              onClick={() => {
                if (selectedInvoice) {
                  handleDownloadReceipt(selectedInvoice);
                }
              }}
            >
              Download Receipt
            </Button>
          )}
          {invoiceDetails?.id && (
            <Button
              startIcon={<Download />}
              onClick={() => handleDownloadPdf(invoiceDetails.id, invoiceDetails.invoiceNumber)}
            >
              Download Invoice PDF
            </Button>
          )}
          {invoiceDetails?.hostedInvoiceUrl && (
            <Button
              startIcon={<OpenInNew />}
              onClick={() => window.open(invoiceDetails.hostedInvoiceUrl, '_blank')}
            >
              View in Stripe
            </Button>
          )}
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Invoices;

